/* global ADV */
import { createSelector } from 'reselect';
import { theme } from '../theme/theme';
import { BOTH_FILTER, INCOME_FILTER, EXPENSES_FILTER } from './TransactionsConstants';

export const transactionsPage = state => state.TransactionsPage;

export const currentViewSelector = createSelector(transactionsPage, state => state.currentView);

export const currentQuerySelector = createSelector(transactionsPage, state => state.query);

export const isEditSelector = createSelector(transactionsPage, state => state.isEdit);

export const currentAccountSelector = createSelector(
  transactionsPage,
  state => state.currentAccount,
);

export const isFilterOpenSelector = createSelector(transactionsPage, state => state.filterOpen);

export const currentPeriodIdSelector = createSelector(
  transactionsPage,
  state => state.currentPeriodId,
);

export const transactionsSelector = createSelector(transactionsPage, state => state.transactions);

export const accountsSelector = createSelector(transactionsPage, state => state.accounts);

export const paginationSelector = createSelector(transactionsPage, state => state.pagination);

export const expenseCategoryOptionsSelector = createSelector(
  transactionsPage,
  state => state.expenseCategoryOptions,
);

export const periodOptionsSelector = createSelector(transactionsPage, state => state.periodOptions);

export const isAddUpdateModalOpen = createSelector(
  transactionsPage,
  state => state.modal.isAddUpdateOpen,
);

export const isDeleteModalOpen = createSelector(
  transactionsPage,
  state => state.modal.isDeleteOpen,
);

export const draftTransaction = createSelector(transactionsPage, state => state.draftTransaction);

export const getMonthsOptions = () => {
  const monthList = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return monthList.map((month, index) => ({
    label: month,
    value: index + 1,
  }));
};

export const getDaysOptions = () => {
  const daysList = Array.from(Array(31).keys());
  return daysList.map(day => ({
    label: (day + 1).toString(),
    value: day + 1,
  }));
};

export const getYearOptions = () => {
  const currentYear = new Date().getUTCFullYear();
  return [
    {
      value: currentYear - 1,
      label: (currentYear - 1).toString(),
    },
    {
      value: currentYear,
      label: currentYear.toString(),
    },
  ];
};

export const formErrors = createSelector(draftTransaction, draft => {
  const requiredFields = ['month', 'year', 'day', 'description', 'category', 'amount'];

  const errorsDictionary = {
    month: 'Month is a required field',
    year: 'Year is a required field',
    day: 'Day is a required field',
    description: 'Description is a required field',
    category: 'Category is a required field',
    amount: 'Amount is a required field',
  };

  return requiredFields.reduce((errors, field) => {
    if (!draft[field] || draft[field].length < 0) {
      return [...errors, errorsDictionary[field]];
    }
    return errors;
  }, []);
});

export const isEdit = createSelector(transactionsPage, state => state.isEdit);

export const errors = createSelector(transactionsPage, state => state.errors);

export const expenseCategoryFilter = createSelector(
  transactionsPage,
  state => state.expenseCategoryFilter,
);

const computeChartColors = num => {
  let groupIndex = 0;
  let shadeIndex = -1; // start at -1 so we can automatically increment off the bat

  if (num > 25) {
    num = 25;
  } // Max shades we can produce with this method

  const remainder = num % 5;
  let remainderIndex = 0; // the amount of remainders we've used
  const loops = Math.ceil(num / 5); // five total groups to loop
  const loopDecrement = remainder > 0 ? 2 : 1; // if there's a remainder, we need to account for the extra loop

  return Array(...Array(num)).map(() => {
    let remainderIncremented = false;

    if (shadeIndex < loops - loopDecrement) {
      shadeIndex++;
    } else if (remainderIndex < remainder) {
      remainderIncremented = true;
      remainderIndex++;
      shadeIndex++;
    } else {
      groupIndex++;
      shadeIndex = 0;
    }
    const color = ADV.settings.color_groups[groupIndex][shadeIndex];

    if (remainderIncremented) {
      groupIndex++;
      shadeIndex = -1;
    }

    return color;
  });
};

export const incomeExpenseSelector = createSelector(transactionsPage, state => state.incomeExpense);

const chartSettings = (title, total, data) => {
  const series = data.length > 0 ? data : [{ name: '', y: 1 }];
  return {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      backgroundColor: 'transparent',
    },
    title: {
      text: title,
      verticalAlign: 'middle',
      align: 'center',
      y: -10,
      style: {
        color: theme.colors.neutral['700'],
        fontSize: '12px',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        overflow: 'hidden',
        width: '50px',
        textAlign: 'center',
        maxWidth: '120px',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        display: 'block',
      },
      useHTML: true,
    },
    subtitle: {
      text: `$${ADV.util.formatValue(total, 'money')}`,
      verticalAlign: 'middle',
      align: 'center',
      y: 10,
      style: {
        color: '#5E5E5E',
        fontSize: '18px',
        fontWeight: 'bold',
      },
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        size: 200,
        innerSize: 125,
        dataLabels: {
          useHTML: true,
          enabled: data.length > 0,
          formatter() {
            return `<div class="chart-label">
                ${this.point.name.split('/').join('/ ')}<br>${Math.round(this.percentage)}%
              </div>`;
          },
          style: { lineHeight: '26px' },
          distance: 20,
          padding: 0,
        },
        cursor: 'pointer',
      },
      series: {
        animation: false,
        dataLabels: {
          allowOverlap: true,
        },
      },
    },
    series: [
      {
        type: 'pie',
        allowPointSelect: true,
        data: series,
      },
    ],
    credits: {
      enabled: false,
    },
    colors: computeChartColors(series.length),
  };
};

const getChartData = (series, title) =>
  series.map(({ Name, Value }) => ({
    name: Name,
    y: +Value * 100,
    sliced: title === Name,
  }));

export const expensesData = createSelector(
  transactionsPage,
  incomeExpenseSelector,
  (state, incomeExpense) => {
    const { expensesData: expensesStateData, selectedCategory } = state;

    const isExpensesSelected = incomeExpense === EXPENSES_FILTER;
    const areExpensesAndIncomeSelected = incomeExpense === BOTH_FILTER;

    if (isExpensesSelected || areExpensesAndIncomeSelected) {
      const expensesCategorySelected = expensesStateData.series.find(
        ({ Name }) => Name === selectedCategory,
      );

      const title = expensesCategorySelected
        ? expensesCategorySelected.Name
        : expensesStateData.defaultTitle;
      const filteredTotal = expensesCategorySelected
        ? expensesCategorySelected.Value * expensesStateData.total
        : expensesStateData.total;

      const data = getChartData(expensesStateData.series, title);
      const chartData = chartSettings(title, filteredTotal, data);

      return {
        chartData,
        isVisible: expensesStateData.isVisible,
        total: expensesStateData.total,
        title,
      };
    }

    const data = getChartData(expensesStateData.series, expensesStateData.defaultTitle);
    const chartData = chartSettings(expensesStateData.defaultTitle, expensesStateData.total, data);

    return {
      chartData,
      isVisible: expensesStateData.isVisible,
      total: expensesStateData.total,
      title: expensesStateData.defaultTitle,
    };
  },
);

export const incomeData = createSelector(
  transactionsPage,
  incomeExpenseSelector,
  (state, incomeExpense) => {
    const { incomeData: incomeStateData, selectedCategory } = state;

    const isIncomeSelected = incomeExpense === INCOME_FILTER;
    const areExpensesAndIncomeSelected = incomeExpense === BOTH_FILTER;

    if (isIncomeSelected || areExpensesAndIncomeSelected) {
      const incomeCategorySelected = incomeStateData.series.find(
        ({ Name }) => Name === selectedCategory,
      );
      const title = incomeCategorySelected
        ? incomeCategorySelected.Name
        : incomeStateData.defaultTitle;
      const filteredTotal = incomeCategorySelected
        ? incomeCategorySelected.Value * incomeStateData.total
        : incomeStateData.total;

      const data = getChartData(incomeStateData.series, title);
      const chartData = chartSettings(title, filteredTotal, data);

      return {
        chartData,
        isVisible: incomeStateData.isVisible,
        total: incomeStateData.total,
        title,
      };
    }

    const data = getChartData(incomeStateData.series, incomeStateData.defaultTitle);
    const chartData = chartSettings(incomeStateData.defaultTitle, incomeStateData.total, data);

    return {
      chartData,
      isVisible: incomeStateData.isVisible,
      total: incomeStateData.total,
      title: incomeStateData.defaultTitle,
    };
  },
);

export const selectedCategory = createSelector(transactionsPage, state => state.selectedCategory);
